import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const SearchHeader = ({ searchQuery, onSearchChange, sortBy, onSortChange, viewMode, onViewModeChange, resultsCount, isLoading }) => {
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  const sortOptions = [
    { value: 'relevance', label: 'Pertinence', icon: 'Star' },
    { value: 'price-asc', label: 'Prix croissant', icon: 'ArrowUp' },
    { value: 'price-desc', label: 'Prix décroissant', icon: 'ArrowDown' },
    { value: 'date-desc', label: 'Plus récent', icon: 'Clock' },
    { value: 'area-desc', label: 'Surface décroissante', icon: 'Square' },
    { value: 'roi-desc', label: 'ROI décroissant', icon: 'TrendingUp' }
  ];

  const currentSort = sortOptions?.find((option) => option?.value === sortBy) || sortOptions?.[0];

  const handleSortSelect = (value) => {
    onSortChange(value);
    setShowSortDropdown(false);
  };

  return (
    <div className="bg-[#111111] rounded-3xl p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 shadow-2xl border-none outline-none">
      
      {/* Search Bar */}
      <div className="relative">
        <Icon
          name="Search"
          size={20}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white/40" 
        />
        {/* Assurez-vous que le composant Input peut accepter ces classes, sinon appliquez-les directement sur le <input> s'il s'agit d'un wrapper */}
        <Input
          type="search"
          placeholder="Rechercher par titre, commune..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e?.target?.value)}
          className="w-full pl-12 pr-4 h-14 bg-white/5 border-none rounded-2xl text-white placeholder-white/40 focus:ring-2 focus:ring-brand-gold text-lg shadow-inner" 
        />
      </div>

      {/* Controls Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        
        {/* Results Count */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          {isLoading ? (
            <div className="flex items-center gap-3 bg-white/5 px-4 py-2 rounded-xl">
              <div className="w-4 h-4 border-2 border-white/20 border-t-brand-gold rounded-full animate-spin"></div>
              <span className="text-xs font-bold uppercase tracking-widest text-white/50">Recherche...</span>
            </div>
          ) : (
            <div className="flex items-baseline gap-2 bg-white/5 px-4 py-2 rounded-xl">
              <span className="font-heading font-bold text-xl text-white">{resultsCount}</span>
              <span className="text-xs font-bold uppercase tracking-widest text-white/50">propriétés</span>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          
          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 h-12 px-4 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-colors border-none font-medium text-sm shadow-sm"
            >
              <Icon name={currentSort?.icon} size={16} className="text-brand-gold" />
              <span className="hidden sm:inline">{currentSort?.label}</span>
              <span className="sm:hidden">Trier</span>
              <Icon name="ChevronDown" size={14} className="text-white/40 ml-1" />
            </button>

            {showSortDropdown && (
              <>
                {/* Backdrop for mobile */}
                <div
                  className="fixed inset-0 z-40 sm:hidden"
                  onClick={() => setShowSortDropdown(false)} 
                />

                <div className="absolute right-0 top-full mt-2 w-56 bg-[#1A1A1A] rounded-2xl shadow-2xl z-50 py-2 border border-white/10">
                  {sortOptions?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => handleSortSelect(option?.value)}
                      className={`w-full flex items-center space-x-3 px-5 py-3 text-left transition-colors ${
                        sortBy === option?.value 
                          ? 'bg-brand-gold/10 text-brand-gold font-bold' 
                          : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <Icon name={option?.icon} size={16} />
                      <span className="text-sm flex-1">{option?.label}</span>
                      {sortBy === option?.value && (
                        <Icon name="Check" size={16} className="text-brand-gold" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex items-center bg-white/5 rounded-xl p-1">
            {[
              { mode: 'grid', icon: 'Grid3X3', title: 'Vue grille' },
              { mode: 'list', icon: 'List', title: 'Vue liste' },
              { mode: 'map', icon: 'Map', title: 'Vue carte' }
            ]?.map(({ mode, icon, title }) => (
              <button
                key={mode}
                onClick={() => onViewModeChange(mode)}
                className={`p-2.5 min-w-[40px] min-h-[40px] rounded-lg transition-colors flex items-center justify-center border-none ${
                  viewMode === mode 
                    ? 'bg-brand-gold text-black shadow-sm' 
                    : 'text-white/40 hover:text-white hover:bg-white/10'
                }`}
                title={title}
              >
                <Icon name={icon} size={18} />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Active Filters Summary (Search term tag) */}
      {searchQuery && (
        <div className="flex items-center flex-wrap gap-2 pt-4 border-t border-white/5 mt-4">
          <Icon name="Filter" size={14} className="text-white/30" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Recherche active :</span>
          <div className="flex items-center gap-2 bg-brand-gold/10 px-3 py-1.5 rounded-full border border-brand-gold/20">
            <span className="text-xs font-bold text-brand-gold">
              "{searchQuery}"
            </span>
            <button
              onClick={() => onSearchChange('')}
              className="w-4 h-4 rounded-full bg-brand-gold/20 flex items-center justify-center hover:bg-brand-gold text-brand-gold hover:text-black transition-colors border-none"
            >
              <Icon name="X" size={10} />
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default SearchHeader;